/**
 * Shared formatting utilities for the application
 */

import {
  CANCELLATION_REASON_LABELS,
  SUBSCRIPTION_CANCELLATION_REASON_LABELS,
} from '../types/cancellationReason.types';

/**
 * Format a number as Canadian currency (CAD)
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(amount);
};

/**
 * Format a dispense quantity for printed receipts/labels (GP-889).
 *
 * Kroll stores DispQty as a decimal (e.g. 2.4 mL, 40, 150.5). The receipt/label
 * code used to `Math.round()` it, so a 2.4 dispense printed as "2". This keeps
 * Kroll's real value while dropping trailing zeros so integers stay clean:
 *   2.4 -> "2.4",  40 -> "40",  2.40 -> "2.4",  150.5 -> "150.5".
 * Accepts a number or a string (a DECIMAL column can serialize as a string).
 */
export const formatDispenseQuantity = (
  qty: number | string | null | undefined,
): string => {
  if (qty === null || qty === undefined || qty === '') return '';
  const n = typeof qty === 'string' ? parseFloat(qty) : qty;
  if (!Number.isFinite(n)) return '';
  // Round to 2 decimals to clear float noise, then drop trailing zeros.
  return String(Math.round(n * 100) / 100);
};

/**
 * Render an action-log / timeline "reason" value as text — PHOX-GSC-PHASE2-4D.
 *
 * order_action_logs.metadata.reason is a string for most writers, but the
 * Edit Order medications-relink flow logs it as a structured object
 * ({ code, notes }). Rendering that object directly as a React child crashed
 * every order-details page whose order carried such a log row ("Objects are
 * not valid as a React child (found: object with keys {code, notes})").
 * This always returns a string: known {code, notes} shapes become
 * "Code Label — notes"; anything else unexpected is JSON-stringified so the
 * audit information is still shown rather than crashing the page.
 */
export const formatReasonText = (reason: unknown): string => {
  if (reason === null || reason === undefined || reason === '') return '';
  if (typeof reason === 'string') return reason;
  if (typeof reason === 'object' && !Array.isArray(reason)) {
    const { code, notes } = reason as { code?: unknown; notes?: unknown };
    const codeText =
      typeof code === 'string' && code
        ? code.replace(/_/g, ' ').trim().replace(/\b\w/g, (c) => c.toUpperCase())
        : '';
    const notesText = typeof notes === 'string' ? notes.trim() : '';
    if (codeText && notesText) return `${codeText} — ${notesText}`;
    if (codeText || notesText) return codeText || notesText;
    if (Object.keys(reason).length === 0) return '';
  }
  try {
    return JSON.stringify(reason) ?? String(reason);
  } catch {
    return String(reason);
  }
};

/**
 * GP-1144: expand raw cancellation reason CODES embedded in timeline / action-log
 * descriptions into their human-readable labels.
 *
 * Backend writers stamp the raw enum value into the audit description, so a
 * Descartes shipment-cancel row reads "Descartes shipment cancelled:
 * Subscription cancelled: ppn" — staff can't tell what "ppn" means. This maps
 * any known reason code (whole-word match) to its label, e.g.
 * "...Subscription cancelled: PPN/FOC". The label maps in
 * cancellationReason.types.ts stay the single source of truth (same labels the
 * cancel dialogs show), so the timeline and the dropdowns never drift.
 *
 * Only the distinctive cancellation-reason slugs (order-only + subscription)
 * are expanded; matching is whole-word so ordinary prose is left untouched.
 */
const REASON_CODE_LABELS: Record<string, string> = {
  ...CANCELLATION_REASON_LABELS,
  ...SUBSCRIPTION_CANCELLATION_REASON_LABELS,
};

const REASON_CODE_PATTERN = new RegExp(
  `\\b(${Object.keys(REASON_CODE_LABELS)
    // Longest first so a code is never partially matched by a shorter one.
    .sort((a, b) => b.length - a.length)
    .map((code) => code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')})\\b`,
  'g',
);

export const expandReasonCodes = (text: unknown): string => {
  if (typeof text !== 'string' || text === '') return '';
  return text.replace(REASON_CODE_PATTERN, (code) => REASON_CODE_LABELS[code] ?? code);
};

/**
 * GP-1334: label a missing prior stage as a data gap, not as a legitimate state.
 *
 * Per-drug stage-change rows are written as "Drug X (Rx N) moved <from> → <to>";
 * when the source stage was lost the backend used to write the literal "(unset)",
 * which reads as "this drug had no previous stage". That is exactly why a drug
 * regressing out of Ready to Ship looked unexplainable on the Order Timeline. The
 * backend now writes "(not recorded)", but historical rows keep the old wording, so
 * the display layer normalizes it too.
 */
export const MISSING_PRIOR_STAGE_LABEL = 'not recorded';

/** Timeline row text as staff should read it: reason codes expanded, gaps named. */
export const formatTimelineDescription = (text: unknown): string =>
  expandReasonCodes(text).replace(/\(unset\)/g, `(${MISSING_PRIOR_STAGE_LABEL})`);

/**
 * Format date to DD/MM/YYYY for display
 * Accepts 'YYYY-MM-DD' | ISO string | Date | NEXI US format ('M/D/YYYY' or
 * 'MM/DD/YYYY' with optional trailing time like 'MM/DD/YYYY HH:MM:SS AM')
 */
export const formatDateForDisplay = (value: string | Date): string => {
  if (!value) return '';

  // Extract YYYY-MM-DD using local time (never .toISOString() which converts to UTC)
  let ymd: string;
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    ymd = `${y}-${m}-${d}`;
  } else {
    const str = String(value);
    // NEXI US format: "M/D/YYYY" or "MM/DD/YYYY" (with optional time suffix)
    const usMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (usMatch) {
      const [, month, day, year] = usMatch;
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }
    ymd = str.split('T')[0];
  }

  const [year, month, day] = ymd.split('-');
  if (!year || !month || !day) return String(value);
  return `${day}/${month}/${year}`;
};

/**
 * Format a stored INSTANT (a UTC ISO timestamp such as
 * `metadata.troubleshootingSentAt`) as the calendar date it happened on locally.
 *
 * Distinct from `formatDateForDisplay`, which takes the leading `YYYY-MM-DD` off a
 * string — correct for a date-only field, but for a real timestamp it shows the UTC
 * day, so anything logged after 8pm reads as tomorrow. Converting through `Date`
 * first is what makes an evening send-back show the evening's date (GP-1294).
 *
 * Returns '' for empty or unparseable input rather than "Invalid Date".
 */
export const formatTimestampDateForDisplay = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  const instant = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(instant.getTime())) return '';
  return formatDateForDisplay(instant);
};

/**
 * Extract the time portion of a NEXI visit date string (GP-611).
 * Handles both the webhook US format ("1/7/2026 9:00:00 AM") and ISO
 * timestamps ("2026-01-07T09:00:00"). Returns '-' when no time is present.
 * Pure string parsing — never constructs a Date, so no UTC conversion.
 */
export const extractVisitTime = (visitDate: string | undefined): string => {
  if (!visitDate) return '-';
  const timeMatch = visitDate.match(/(\d{1,2}:\d{2}:\d{2}\s+(?:AM|PM))/i);
  if (timeMatch) {
    return timeMatch[1];
  }
  const isoMatch = visitDate.match(/T(\d{2}:\d{2})/);
  if (isoMatch) {
    return isoMatch[1];
  }
  return '-';
};
